import { checkGrid } from './check-grid.ts';
import {
	answerCells,
	cellKind,
	clueAnchor,
	coordKey,
	formatCoord,
	inBounds,
	sameCoord,
	solutionLetter,
	step,
} from './grid.ts';
import type { Issue } from './issues.ts';
import { isClueDirection, parsePuzzle } from './parse.ts';
import type { Answer, Axis, Clue, Coord, Puzzle } from './types.ts';

const ANSWER_TEXT = /^[A-Z]+$/;

/**
 * Semantic checks: is this a well-formed, solvable Arrowword? Assumes the structure is
 * sound (run `parsePuzzle` first on untrusted input) and returns every issue it finds,
 * in a stable order, rather than stopping at the first.
 */
export function validatePuzzle(puzzle: Puzzle): Issue[] {
	// Every other check reads cells; on a misshapen grid they would only add noise.
	const gridIssues = checkGrid(puzzle);
	if (gridIssues.length > 0) return gridIssues;

	const issues: Issue[] = [];
	const answersById = indexById(puzzle.answers, 'answers', 'duplicate-answer-id', issues);
	indexById(puzzle.clues, 'clues', 'duplicate-clue-id', issues);

	const coverage = checkAnswers(puzzle, issues);
	checkClues(puzzle, answersById, issues);
	checkCoverage(puzzle, coverage, issues);
	checkTheme(puzzle, answersById, issues);

	return issues;
}

/** Parse untrusted JSON, then validate it: every issue from both passes. */
export function validatePuzzleJson(raw: unknown): Issue[] {
	const parsed = parsePuzzle(raw);
	return parsed.ok ? validatePuzzle(parsed.puzzle) : parsed.issues;
}

// --- Ids --------------------------------------------------------------------------------

/** Maps id → first item with that id, reporting any later repeats. */
function indexById<T extends { id: string }>(
	items: readonly T[],
	field: 'answers' | 'clues',
	code: 'duplicate-answer-id' | 'duplicate-clue-id',
	issues: Issue[],
): Map<string, T> {
	const byId = new Map<string, T>();
	items.forEach((item, i) => {
		if (byId.has(item.id)) {
			issues.push({
				code,
				message: `${field === 'answers' ? 'Answer' : 'Clue'} id "${item.id}" is used more than once.`,
				path: `${field}[${i}].id`,
				ids: [item.id],
			});
		} else {
			byId.set(item.id, item);
		}
	});
	return byId;
}

// --- Answers ----------------------------------------------------------------------------

/** Which answers cover each letter cell, per axis, keyed by `coordKey`. */
interface Coverage {
	across: Map<string, string[]>;
	down: Map<string, string[]>;
}

function checkAnswers(puzzle: Puzzle, issues: Issue[]): Coverage {
	const coverage: Coverage = { across: new Map(), down: new Map() };
	// For crossing conflicts: the letters each answer claims for each cell.
	const claims = new Map<string, { id: string; letter: string; cell: Coord }[]>();
	const placements = new Map<string, string>();

	puzzle.answers.forEach((answer, i) => {
		const path = `answers[${i}]`;
		const label = `Answer "${answer.id}"`;

		const textOk = ANSWER_TEXT.test(answer.text);
		if (!textOk) {
			issues.push({
				code: 'answer-text-invalid',
				message: `${label} is ${JSON.stringify(answer.text)}; answers are uppercase A–Z only, no spaces.`,
				path: `${path}.text`,
				ids: [answer.id],
			});
		} else if (answer.text.length < 2) {
			issues.push({
				code: 'answer-too-short',
				message: `${label} has ${answer.text.length} letter; answers need at least 2.`,
				path: `${path}.text`,
				ids: [answer.id],
			});
		}

		if (answer.display !== undefined && textOk) {
			// The display form may add spaces and punctuation, but not change the letters.
			const letters = answer.display.toUpperCase().replace(/[^A-Z]/g, '');
			if (letters !== answer.text) {
				issues.push({
					code: 'display-mismatch',
					message: `${label}'s display form "${answer.display}" doesn't spell ${answer.text}.`,
					path: `${path}.display`,
					ids: [answer.id],
				});
			}
		}

		if (answer.axis !== 'across' && answer.axis !== 'down') {
			issues.push({
				code: 'malformed-axis',
				message: `${label} has axis ${JSON.stringify(answer.axis)}; use "across" or "down".`,
				path: `${path}.axis`,
				ids: [answer.id],
			});
			return;
		}

		if (!inBounds(puzzle, answer.start)) {
			issues.push({
				code: 'coord-out-of-bounds',
				message: `${label} starts at ${formatCoord(answer.start)}, outside the ${puzzle.width}×${puzzle.height} grid.`,
				path: `${path}.start`,
				cell: answer.start,
				ids: [answer.id],
			});
			return;
		}

		const cells = answerCells(answer);
		const blocked = cells.find((cell) => cellKind(puzzle, cell) !== 'letter');
		if (blocked) {
			const kind = cellKind(puzzle, blocked);
			issues.push({
				code: 'answer-path-impossible',
				message:
					kind === undefined
						? `${label} (${answer.text.length} letters ${answer.axis}) runs off the grid at ${formatCoord(blocked)}.`
						: `${label} runs through ${kind === 'clue' ? 'a clue' : 'a blank'} cell at ${formatCoord(blocked)}.`,
				path,
				cell: blocked,
				ids: [answer.id],
			});
		}

		cells.forEach((cell, n) => {
			if (cellKind(puzzle, cell) !== 'letter') return;
			const key = coordKey(cell);
			const covering = coverage[answer.axis].get(key) ?? [];
			covering.push(answer.id);
			coverage[answer.axis].set(key, covering);

			const letter = answer.text[n] ?? '';
			const claim = claims.get(key) ?? [];
			claim.push({ id: answer.id, letter, cell });
			claims.set(key, claim);

			const expected = solutionLetter(puzzle, cell);
			if (textOk && letter !== expected) {
				issues.push({
					code: 'answer-letter-mismatch',
					message: `${label} puts ${letter} at ${formatCoord(cell)}, but the grid has ${expected}.`,
					path: `${path}.text`,
					cell,
					ids: [answer.id],
				});
			}
		});

		// An Arrowword answer fills its whole run of letters; a neighbour letter on
		// either end means the answer is the wrong length or starts in the wrong place.
		if (!blocked && cells.length > 0) {
			const before = step(answer.start, answer.axis, -1);
			if (cellKind(puzzle, before) === 'letter') {
				issues.push({
					code: 'answer-starts-mid-run',
					message: `${label} starts at ${formatCoord(answer.start)}, but the letter run continues back to ${formatCoord(before)}.`,
					path: `${path}.start`,
					cell: before,
					ids: [answer.id],
				});
			}
			const after = step(answer.start, answer.axis, cells.length);
			if (cellKind(puzzle, after) === 'letter') {
				issues.push({
					code: 'answer-ends-mid-run',
					message: `${label} is ${cells.length} letters, but its letter run continues to ${formatCoord(after)}.`,
					path: `${path}.text`,
					cell: after,
					ids: [answer.id],
				});
			}
		}

		const placement = `${coordKey(answer.start)}|${answer.axis}|${answer.text.length}`;
		const twin = placements.get(placement);
		if (twin !== undefined) {
			issues.push({
				code: 'duplicate-answer-placement',
				message: `${label} occupies exactly the same cells as answer "${twin}".`,
				path,
				cell: answer.start,
				ids: [twin, answer.id],
			});
		} else {
			placements.set(placement, answer.id);
		}
	});

	for (const claim of claims.values()) {
		const letters = new Set(claim.map((c) => c.letter));
		const first = claim[0];
		if (letters.size > 1 && first) {
			issues.push({
				code: 'crossing-conflict',
				message: `Answers disagree at ${formatCoord(first.cell)}: ${claim.map((c) => `"${c.id}" has ${c.letter}`).join(', ')}.`,
				cell: first.cell,
				ids: claim.map((c) => c.id),
			});
		}
	}

	return coverage;
}

// --- Clues ------------------------------------------------------------------------------

function checkClues(puzzle: Puzzle, answersById: Map<string, Answer>, issues: Issue[]): void {
	const cluesByCell = new Map<string, Clue[]>();
	const cluesByAnswer = new Map<string, Clue[]>();

	puzzle.clues.forEach((clue, i) => {
		const path = `clues[${i}]`;
		const label = `Clue "${clue.id}"`;

		if (!inBounds(puzzle, clue.cell)) {
			issues.push({
				code: 'coord-out-of-bounds',
				message: `${label} sits at ${formatCoord(clue.cell)}, outside the ${puzzle.width}×${puzzle.height} grid.`,
				path: `${path}.cell`,
				cell: clue.cell,
				ids: [clue.id],
			});
		} else {
			if (cellKind(puzzle, clue.cell) !== 'clue') {
				issues.push({
					code: 'clue-not-in-clue-cell',
					message: `${label} sits at ${formatCoord(clue.cell)}, which is not a "#" clue cell.`,
					path: `${path}.cell`,
					cell: clue.cell,
					ids: [clue.id],
				});
			}
			const key = coordKey(clue.cell);
			cluesByCell.set(key, [...(cluesByCell.get(key) ?? []), clue]);
		}

		const answer = answersById.get(clue.answerId);
		if (!answer) {
			issues.push({
				code: 'clue-missing-answer',
				message: `${label} points to answer "${clue.answerId}", which doesn't exist.`,
				path: `${path}.answerId`,
				ids: [clue.id],
			});
		} else {
			cluesByAnswer.set(answer.id, [...(cluesByAnswer.get(answer.id) ?? []), clue]);
		}

		if (!isClueDirection(clue.direction)) {
			issues.push({
				code: 'malformed-direction',
				message: `${label} has direction ${JSON.stringify(clue.direction)}.`,
				path: `${path}.direction`,
				ids: [clue.id],
			});
		} else if (answer) {
			const anchor = clueAnchor(clue.direction, clue.cell);
			if (!sameCoord(anchor.start, answer.start) || anchor.axis !== answer.axis) {
				issues.push({
					code: 'clue-direction-mismatch',
					message: `${label}'s "${clue.direction}" arrow points to ${anchor.axis} from ${formatCoord(anchor.start)}, but answer "${answer.id}" runs ${answer.axis} from ${formatCoord(answer.start)}.`,
					path: `${path}.direction`,
					cell: clue.cell,
					ids: [clue.id, answer.id],
				});
			}
		}
	});

	for (const clues of cluesByCell.values()) {
		const cell = clues[0]?.cell;
		if (!cell) continue;
		const ids = clues.map((c) => c.id);
		if (clues.length > 2) {
			issues.push({
				code: 'clue-cell-overfull',
				message: `Clue cell ${formatCoord(cell)} holds ${clues.length} clues; it can fit at most 2.`,
				cell,
				ids,
			});
		}
		const directions = clues.map((c) => c.direction);
		const repeated = directions.filter((d, i) => directions.indexOf(d) !== i);
		for (const direction of new Set(repeated)) {
			issues.push({
				code: 'clue-cell-duplicate-direction',
				message: `Clue cell ${formatCoord(cell)} has more than one "${direction}" arrow.`,
				cell,
				ids: clues.filter((c) => c.direction === direction).map((c) => c.id),
			});
		}
	}

	puzzle.answers.forEach((answer, i) => {
		const clues = cluesByAnswer.get(answer.id) ?? [];
		// Duplicate answer ids are reported already; judge only the first holder of an id.
		if (answersById.get(answer.id) !== answer) return;
		if (clues.length === 0) {
			issues.push({
				code: 'answer-without-clue',
				message: `Answer "${answer.id}" has no clue.`,
				path: `answers[${i}]`,
				ids: [answer.id],
			});
		} else if (clues.length > 1) {
			issues.push({
				code: 'answer-multiple-clues',
				message: `Answer "${answer.id}" has ${clues.length} clues; it needs exactly one.`,
				path: `answers[${i}]`,
				ids: [answer.id, ...clues.map((c) => c.id)],
			});
		}
	});

	// A "#" cell promises a clue; an empty one should be "." instead.
	forEachCell(puzzle, (cell) => {
		if (cellKind(puzzle, cell) === 'clue' && !cluesByCell.has(coordKey(cell))) {
			issues.push({
				code: 'orphan-clue-cell',
				message: `Clue cell ${formatCoord(cell)} holds no clue; mark an empty cell "." instead.`,
				cell,
			});
		}
	});
}

// --- Coverage ---------------------------------------------------------------------------

function checkCoverage(puzzle: Puzzle, coverage: Coverage, issues: Issue[]): void {
	forEachCell(puzzle, (cell) => {
		if (cellKind(puzzle, cell) !== 'letter') return;
		const key = coordKey(cell);
		if (!coverage.across.has(key) && !coverage.down.has(key)) {
			issues.push({
				code: 'orphan-letter-cell',
				message: `Letter cell ${formatCoord(cell)} is not part of any answer.`,
				cell,
			});
		}
	});

	// In an Arrowword every run of two or more letters is a word. A run that no answer
	// covers along its own axis would be letters the player can never be clued for.
	for (const axis of ['across', 'down'] as const) {
		for (const run of letterRuns(puzzle, axis)) {
			if (run.length < 2 || run.some((cell) => coverage[axis].has(coordKey(cell)))) continue;
			const [first] = run;
			if (!first) continue;
			issues.push({
				code: 'unclued-run',
				message: `The ${run.length} letters ${axis} from ${formatCoord(first)} form a word with no answer.`,
				cell: first,
			});
		}
	}
}

/** Maximal runs of consecutive letter cells along `axis`, each in reading order. */
function letterRuns(puzzle: Puzzle, axis: Axis): Coord[][] {
	const runs: Coord[][] = [];
	const lines = axis === 'across' ? puzzle.height : puzzle.width;
	const length = axis === 'across' ? puzzle.width : puzzle.height;
	for (let line = 0; line < lines; line++) {
		let run: Coord[] = [];
		for (let i = 0; i <= length; i++) {
			const cell = axis === 'across' ? { row: line, col: i } : { row: i, col: line };
			if (cellKind(puzzle, cell) === 'letter') {
				run.push(cell);
			} else {
				if (run.length > 0) runs.push(run);
				run = [];
			}
		}
	}
	return runs;
}

function forEachCell(puzzle: Puzzle, visit: (cell: Coord) => void): void {
	for (let row = 0; row < puzzle.height; row++) {
		for (let col = 0; col < puzzle.width; col++) visit({ row, col });
	}
}

// --- Theme ------------------------------------------------------------------------------

function checkTheme(puzzle: Puzzle, answersById: Map<string, Answer>, issues: Issue[]): void {
	puzzle.theme?.members.forEach((id, i) => {
		if (!answersById.has(id)) {
			issues.push({
				code: 'theme-unknown-answer',
				message: `Theme member "${id}" is not an answer id.`,
				path: `theme.members[${i}]`,
				ids: [id],
			});
		}
	});
}
